<script setup lang="ts">
import { CirclePlus, Fold, Search } from "@element-plus/icons-vue";
import { computed, onMounted, ref } from "vue";
import { Entity, EntityCreateDto, EntityDefinitionDto } from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { apiClient } from "~/utils/apiClient";
// import { useStorage } from "@vueuse/core";
import { useRoute } from "vue-router";
import { plural } from "~/utils/miscUtils";
import EntityInstanceCreator from "../components/EntityInstanceCreator.vue";
import EntityResponseViewer from "../components/EntityResponseViewer.vue";

// import UserResponsesViewer from "~/Compliance/components/UserResponsesViewer.vue";
const route = useRoute();
const myInfoStore = useMyInfoStore();
const entities = ref<localEntityDto[]>([]);
const orgInfoStore = useOrgInfoStore();

const loading = ref(true);
const searchQuery = ref<string>("");

const entityDefinitionGuid: string = `${(route.meta.entityGuid as string) ?? ""}`;
const currentEntityDefinitionDto = ref<EntityDefinitionDto | null>(null);

const entityDefinitionName = computed(
  () => currentEntityDefinitionDto.value?.name ?? "Entity",
);
const entityDefinitionNamePlural = computed(
  () => currentEntityDefinitionDto.value?.plural ?? "Entities",
);

export type localEntityDto = Entity;

const filteredEntities = computed(() => {
  const searchQueryLowered = searchQuery.value.toLowerCase();
  const searchFiltered =
    searchQueryLowered !== "" && searchQueryLowered !== undefined;

  return entities.value.filter((entity: localEntityDto) => {
    if (searchFiltered) {
      const name = entity.name?.toLowerCase().includes(searchQueryLowered);
      if (!name) {
        return false;
      }
    }

    return true;
  });
});

let retrieving = false;
const retrieveEntities = async () => {
  if (retrieving) {
    // may be called twice in a row
    return;
  }

  retrieving = true;

  // TODO: change API to accept entityName
  entities.value = (
    await apiClient.entity.getEntities(
      myInfoStore.currentSysOrgGuid,
      entityDefinitionGuid,
    )
  ).map(extendEntity);
  retrieving = false;
};

const entityDialogOpen = ref(false);
const entityBeingCreated = ref<EntityCreateDto>();
const entityOpenCounter = ref(0);

const createEntity = () => {
  entityBeingCreated.value = undefined;
  entityOpenCounter.value++;
  entityDialogOpen.value = true;
};

const handleAddEntity = (wf: localEntityDto) => {
  entities.value.push(wf);
  showViewer(wf);
};

const showViewer = (row: localEntityDto) => {
  selectedEntity.value = row;
  drawerOpen.value = true;
};

const loadData = async () => {
  loading.value = true;
  // done in order... myInfoStore must be loaded before retrieving entities
  await myInfoStore.load();

  // await prepareOrgs();
  await retrieveEntities();
  loading.value = false;
};

//For the drawer
const drawerOpen = ref(false);
const selectedEntity = ref<localEntityDto | null>(null);

const handleCloseViewer = () => {
  selectedEntity.value = null;
  drawerOpen.value = false;
};

const extendEntity = (entity: Entity): localEntityDto => {
  return { ...entity };
};

onMounted(async () => {
  await orgInfoStore.load();
  // get the dto for the guid given
  if (entityDefinitionGuid) {
    // look recursively for the entity definition
    currentEntityDefinitionDto.value =
      orgInfoStore.MyOrgInfo?.entityDefinitionDtos.reduce(
        (acc: EntityDefinitionDto | null, e: EntityDefinitionDto) => {
          if (acc) {
            return acc;
          }
          if (e.guid === entityDefinitionGuid) {
            return e;
          }
          // check recursively
          return (
            e.childEntityDefinitions?.find(
              (child) => child.guid === entityDefinitionGuid,
            ) || null
          );
        },
        null,
      ) ?? null;
    if (currentEntityDefinitionDto.value) {
      await loadData();
    }
  }
});
</script>
<template>
  <div class="MainList">
    <div class="list-header">
      <div v-if="!loading" class="flexRow">
        <el-input
          v-model="searchQuery"
          class="search"
          clearable
          placeholder="Search"
          :prefix-icon="Search"
        />
        <RefreshButton class="refresh" @click="loadData" />
        <span class="showingInTable">
          Showing
          {{ filteredEntities.length }}
          <span v-if="entities.length > 1"> of {{ entities.length }}</span>
          {{
            plural(
              entities.length,
              entityDefinitionName,
              entityDefinitionNamePlural,
            )
          }}
        </span>

        <el-button
          class="addButton"
          :icon="CirclePlus"
          title="Create a new entity"
          type="primary"
          style="font-size: 15px"
          @click="createEntity"
        >
          New {{ entityDefinitionName }}
        </el-button>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="filteredEntities"
      class="EntityTable"
      height="90vh"
      :default-sort="{ prop: 'loginName', order: 'ascending' }"
    >
      <el-table-column :label="entityDefinitionName" width="300" sortable>
        <template #default="scope">
          <el-link
            :icon="Fold"
            type="primary"
            style="font-size: 15px"
            @click="showViewer(scope.row)"
          >
            {{ scope.row.name }}
          </el-link>
        </template>
      </el-table-column>
    </el-table>
  </div>

  <EntityInstanceCreator
    :key="entityOpenCounter"
    v-model="entityDialogOpen"
    :user-guid="undefined"
    :entity-definition-guid="entityDefinitionGuid"
    :entity-definition-name="entityDefinitionName"
    @add:entity="handleAddEntity"
  />

  <EntityResponseViewer
    v-if="selectedEntity?.guid"
    :key="selectedEntity.guid"
    :entity="selectedEntity"
    @close-drawer="handleCloseViewer"
  />
</template>
<style lang="less">
.MainList {
  .showingInTable {
    width: fit-content;
    padding: 0 1em;
  }

  .list-header {
    .icon {
      cursor: pointer;
      margin-left: 0.5em;
      color: var(--el-color-primary);
      font-size: 1.2em;
      vertical-align: text-bottom;
    }

    .ep-table .recently-changed {
      --ep-table-tr-bg-color: var(--ep-color-primary-light-7) !important;
    }
    .ep-table {
      // height: 75vh;
      --ep-table-border-color: #c1c2c3;
    }

    .filters {
      display: grid;
      grid-template-areas:
        "search m g x"
        "showing y y r";
      grid-template-columns: repeat(3, auto) 1fr;
      gap: 2em 1em;
      .showingInTable {
        grid-area: showing;
      }
      .refresh {
        grid-area: r;
        justify-self: end;
      }
    }

    .flexRow {
      align-items: center;
      .refreshButton {
        margin: 0;
      }
    }

    .orgFilter {
      display: flex;
      align-items: center;
      gap: 5px;
      .el-select {
        width: 200px;
      }
    }
  }
  .EntityTable {
    // .orgList {
    //   padding: 0 0 0 2em;
    //   margin: 0;
    // }
    .el-tag + .el-tag {
      margin-left: 5px;
    }
    .el-icon {
      margin-left: 10px;
    }
  }
}
</style>
